import React, { useEffect, useState } from "react";
import { Backdrop, CircularProgress } from "@mui/material";
import api from "../api/api";
import ToastMessage from "../utils/mui/righttoptoast";
import { getPracticeId } from "../config/practice";

// Onboarding > "Unmatched patient mapping": link legacy Dallas (1111)
// patients to their Athena patientid. Entering an id and clicking Link
// approves the crosswalk row server-side and migrates everything for that
// patient (notes + files, appointments, documents) in one shot.
const UnmatchedMapping = () => {
  const practiseid = getPracticeId();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(null); // old_patientid in flight
  const [inputs, setInputs] = useState({});
  const [results, setResults] = useState({}); // old_patientid -> summary
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [alertmessage, setAlertMessage] = useState("");
  const [severity, setSeverity] = useState("success");

  const fetchList = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/v1/${practiseid}/onboarding/dallas-mapping/list`,
      );
      if (response.status === 200) setRows(response.data || []);
    } catch (e) {
      console.error("mapping list failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleLink = async (row, confirmCreate = false) => {
    const newId = (inputs[row.old_patientid] || "").trim();
    if (!/^\d+$/.test(newId)) {
      setAlertMessage("Enter a numeric Athena patient id");
      setSeverity("info");
      setOpen(true);
      return;
    }
    setLinking(row.old_patientid);
    try {
      const response = await api.post(
        `/v1/${practiseid}/onboarding/dallas-mapping/link`,
        {
          old_patientid: row.old_patientid,
          new_patientid: Number(newId),
          ...(confirmCreate && { confirm_create: true }),
        },
      );
      if (response.status === 200) {
        const d = response;
        setResults((p) => ({ ...p, [row.old_patientid]: d }));
        setAlertMessage(
          `${row.name} linked to ${newId} — ${d.notes_remapped} notes, ` +
            `${d.appointments} appts, ${d.documents} docs migrated`,
        );
        setSeverity("success");
        setOpen(true);
      } else {
        setAlertMessage(response?.message || "Link failed");
        setSeverity("error");
        setOpen(true);
      }
    } catch (error) {
      const body = error?.response?.data;
      // Unknown Athena id — ask for explicit confirmation before creating.
      if (body?.needs_confirm) {
        setLinking(null);
        if (
          window.confirm(
            `${body.message}\n\nLink ${row.name} to ${newId} anyway?`,
          )
        ) {
          return handleLink(row, true);
        }
        return;
      }
      setAlertMessage(body?.message || "Something went wrong");
      setSeverity("error");
      setOpen(true);
    } finally {
      setLinking(null);
    }
  };

  const visible = rows.filter(
    (r) =>
      !searchTerm ||
      r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(r.old_patientid).includes(searchTerm) ||
      (r.phone || "").includes(searchTerm),
  );

  return (
    <div>
      <div className="w-full flex justify-between items-center mb-3">
        <div className="text-sm text-gray-600">
          Legacy Dallas patients not yet linked to Athena. Create the patient
          in Athena first if they don't exist, then enter the Athena patient
          id and Link — notes, appointments and documents migrate
          automatically.
        </div>
        <input
          type="text"
          className="bg-[#F5F6FA] border border-[#D5D5D5] rounded-[10px] py-1 px-3 text-sm outline-none"
          placeholder="Search name / id / phone"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="overflow-auto rounded-t-2xl shadow-md max-h-[520px]">
        <table className="min-w-full table-auto border-collapse">
          <thead>
            <tr
              className="text-left text-sm sticky top-0 text-white"
              style={{ borderBottom: "1px solid #E4E4E7", background: "#50869A" }}
            >
              <th className="py-2 px-3">1111 id</th>
              <th className="py-2 px-3">Name</th>
              <th className="py-2 px-3">DOB</th>
              <th className="py-2 px-3">Phone</th>
              <th className="py-2 px-3">Appts</th>
              <th className="py-2 px-3">Notes</th>
              <th className="py-2 px-3">Suggestions (same DOB)</th>
              <th className="py-2 px-3">Athena patient id</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const done = results[row.old_patientid];
              return (
                <tr
                  key={row.old_patientid}
                  className="text-sm border-b border-[#E4E4E7] hover:bg-neutral-50"
                >
                  <td className="py-2 px-3">{row.old_patientid}</td>
                  <td className="py-2 px-3 font-medium">{row.name}</td>
                  <td className="py-2 px-3">{row.dob}</td>
                  <td className="py-2 px-3">{row.phone}</td>
                  <td className="py-2 px-3">{row.appts}</td>
                  <td className="py-2 px-3">{row.notes}</td>
                  <td className="py-2 px-3">
                    {row.candidates.map((c) => (
                      <button
                        key={c.patientid}
                        title={`DOB ${c.dob}`}
                        className="mr-1 mb-1 px-2 py-0.5 rounded-full border border-[#50869A] text-[#50869A] text-xs hover:bg-[#50869A] hover:text-white"
                        onClick={() =>
                          setInputs((p) => ({
                            ...p,
                            [row.old_patientid]: String(c.patientid),
                          }))
                        }
                      >
                        {c.patientid} · {c.name}
                      </button>
                    ))}
                  </td>
                  <td className="py-2 px-3">
                    {done ? (
                      <span className="text-green-700 text-xs font-semibold">
                        Linked → {done.new_patientid} ({done.notes_remapped}{" "}
                        notes, {done.appointments} appts, {done.documents} docs)
                      </span>
                    ) : (
                      <input
                        type="text"
                        className="w-24 border border-[#D5D5D5] rounded px-2 py-1 text-sm outline-none"
                        value={inputs[row.old_patientid] || ""}
                        onChange={(e) =>
                          setInputs((p) => ({
                            ...p,
                            [row.old_patientid]: e.target.value,
                          }))
                        }
                      />
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {!done && (
                      <button
                        disabled={linking !== null}
                        className="px-3 py-1 rounded bg-[#50869A] text-white text-sm disabled:opacity-40"
                        onClick={() => handleLink(row)}
                      >
                        {linking === row.old_patientid ? "Migrating…" : "Link"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!visible.length && !loading && (
              <tr>
                <td colSpan={9} className="py-6 text-center text-gray-500">
                  No unmatched patients — everything is linked.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || linking !== null}
      >
        <div className="flex flex-col items-center gap-2">
          <CircularProgress color="inherit" />
          {linking !== null && (
            <span className="text-sm">
              Migrating notes, appointments and documents…
            </span>
          )}
        </div>
      </Backdrop>
      <ToastMessage
        open={open}
        handleClose={() => setOpen(false)}
        message={alertmessage}
        severity={severity}
      />
    </div>
  );
};

export default UnmatchedMapping;
